/**
 * Onboarding V2 — the model layer.
 *
 * Everything that decides WHAT the user is asked, in WHICH order, and what
 * their answers mean lives here. It is deliberately I/O-free apart from
 * `localStorage`, so the branch matrix and the readiness tables are unit
 * testable without mounting a single component.
 *
 * Invariants defended here (breaking any of these regresses the flow):
 *  1. `'success'` is never part of a step sequence — it is a terminal
 *     post-Meta screen, reachable only by `?resume=success` or a real Meta
 *     return.
 *  2. `business-type` is always the 2nd step, because the industry
 *     personalizes every later preview.
 *  3. Instagram adds no questions — it needs no number preparation.
 *  4. `otherPlatform: 'unsure'` is `ready`, not a blocker. We defer to Meta
 *     rather than pretending to identify the provider.
 *  5. An incompatible stored draft THROWS so the page can render a recovery
 *     screen. A corrupt draft must degrade, never silently reset answers.
 *
 * REMOVED (2026-08-05), two questions and their whole dimensions:
 *
 *  - **number-use** ("How are you using this number today?"). Asking a business
 *    owner which WhatsApp app their number sits in put a support question in
 *    front of a signup, and Meta reports the number's real state during the
 *    connection anyway.
 *  - **otp-method** ("Can this phone receive an OTP?"). Meta chooses and sends
 *    the code itself; asking in advance neither changed what we did nor what
 *    Meta did, and "not sure" only manufactured a blocker.
 *
 * In both cases the whole dimension went with the question. A question we do
 * not ask cannot honestly produce a readiness row or a guidance card, and
 * leaving one unanswered would have parked every WhatsApp user behind a
 * permanent "Confirm…" blocker. Stored drafts that still carry a `numberUse` or
 * `otpMethod` key are harmless: the extra properties are ignored on read and
 * nothing derives from them.
 */

export type TernaryAnswer = 'yes' | 'no' | 'unsure';

export type OnboardingGoal =
  | 'whatsapp-automation'
  | 'instagram-automation'
  | 'campaigns-broadcasts'
  | 'meta-ads'
  | 'social-scheduling'
  | 'all-features';

/** Every selectable product goal, excluding the "all features" shortcut. */
export const FEATURE_GOALS: readonly OnboardingGoal[] = [
  'whatsapp-automation',
  'instagram-automation',
  'campaigns-broadcasts',
  'meta-ads',
  'social-scheduling',
] as const;

/**
 * Expands `all-features` into the full product set so readiness / setup-plan
 * helpers stay simple. The stored answer may still carry the shortcut id.
 */
export function expandGoals(goals: readonly OnboardingGoal[] = []): OnboardingGoal[] {
  if (!goals.includes('all-features')) return [...goals];
  return Array.from(new Set<OnboardingGoal>([...goals, ...FEATURE_GOALS]));
}

/**
 * Goals shown on `/onboarding/dashboard` "What would you like to do first?".
 *
 * Exact step-1 selection only — never pads with contacts / launch stand-ins.
 * Skip (empty / missing goals) and the `all-features` shortcut both expand to
 * every product goal so the plan matches the goals screen the user skipped.
 * Catalog order is preserved so the cards never reshuffle on revisit.
 */
export function resolveSetupPlanGoals(
  goals: readonly OnboardingGoal[] | null | undefined
): OnboardingGoal[] {
  const source =
    !goals || goals.length === 0 ? (['all-features'] as const) : goals;
  const expanded = new Set(
    expandGoals(source).filter(
      (g) => g !== 'all-features' && (FEATURE_GOALS as readonly string[]).includes(g)
    )
  );
  return FEATURE_GOALS.filter((g) => expanded.has(g));
}

export type BusinessIndustry =
  | 'fashion'
  | 'beauty'
  | 'food'
  | 'healthcare'
  | 'education'
  | 'real-estate'
  | 'services'
  | 'other';

export type OnboardingV2StepId =
  | 'goals'
  | 'business-type'
  | 'other-platform'
  | 'platform-name'
  | 'summary'
  | 'success';

export interface OnboardingV2Answers {
  /** Multi-select. */
  goals?: OnboardingGoal[];
  /** Drives every personalized preview. */
  industry?: BusinessIndustry;
  /**
   * Free-text business type when `industry === 'other'`. Required to complete
   * the business-type step; cleared whenever a named industry is selected.
   */
  industryOther?: string;
  otherPlatform?: TernaryAnswer;
  /** A `PLATFORM_OPTIONS` value. */
  platformName?: string;
}

/** Catch-all industry card — selecting it opens the custom-type field. */
export const INDUSTRY_OTHER_VALUE: BusinessIndustry = 'other';

/** Soft caps for the custom business-type field. */
export const INDUSTRY_OTHER_MIN_LENGTH = 2;
export const INDUSTRY_OTHER_MAX_LENGTH = 80;

/**
 * Trim + length-gate the custom industry label. Whitespace-only and 1-char
 * answers are treated as empty so Continue stays blocked until something
 * real is typed.
 */
export function sanitizeIndustryOther(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim().slice(0, INDUSTRY_OTHER_MAX_LENGTH);
  if (trimmed.length < INDUSTRY_OTHER_MIN_LENGTH) return undefined;
  return trimmed;
}

export interface OnboardingV2Draft {
  version: number;
  answers: OnboardingV2Answers;
  currentStep: OnboardingV2StepId;
  /** ISO timestamp of the last write. */
  updatedAt: string;
}

export const ONBOARDING_V2_STORAGE_VERSION = 2;

export const ONBOARDING_V2_DRAFT_KEY = 'wenext:onboarding-v2:draft';
export const ONBOARDING_V2_CONNECTED_KEY = 'wenext:onboarding-v2:connected';
export const INSTAGRAM_CONNECTED_AT_KEY = 'instagram:connected-at';

/** Every id a `?resume=` param may name. */
export const VALID_RESUME_STEPS: readonly OnboardingV2StepId[] = [
  'goals',
  'business-type',
  'other-platform',
  'platform-name',
  'summary',
  'success',
];

export const INCOMPATIBLE_DRAFT_MESSAGE =
  'This saved setup is no longer compatible.';

/* ------------------------------------------------------------------ *
 * Option catalogs
 * ------------------------------------------------------------------ */

export interface OptionDef<T extends string> {
  value: T;
  label: string;
  description?: string;
  /** lucide-react icon name; resolved by the view layer. */
  icon: string;
  /** Optional image (brand mark or illustration). Wins over `icon` when set. */
  iconSrc?: string;
  /** Short capability chips shown under the description (goal cards). */
  features?: readonly string[];
}

export const ONBOARDING_GOALS: readonly OptionDef<OnboardingGoal>[] = [
  {
    value: 'whatsapp-automation',
    label: 'WhatsApp Automation',
    description: 'Automate replies, follow-ups and customer conversations.',
    icon: 'Bot',
    iconSrc: '/assets/social-media/whatsapp-icon.svg',
    features: ['Automate replies', 'Follow-ups', 'Customer conversations'],
  },
  {
    value: 'instagram-automation',
    label: 'Instagram Automation',
    description: 'Automatically respond to Instagram messages and enquiries.',
    icon: 'Instagram',
    iconSrc: '/assets/social-media/instagram-icon.svg',
    features: ['Comment to DM', 'DM replies', 'Story replies'],
  },
  {
    value: 'campaigns-broadcasts',
    label: 'Marketing Campaigns',
    description: 'Send promotional campaigns, updates and offers.',
    icon: 'Megaphone',
    iconSrc: '/onboardingimg/campaign.png',
    features: ['Broadcasts', 'Templates', 'Scheduling'],
  },
  {
    value: 'meta-ads',
    label: 'Meta Ads',
    description: 'Create and manage Meta ad campaigns.',
    icon: 'Target',
    iconSrc: '/assets/social-media/meta-icon.svg',
    features: ['Ad campaigns', 'Audiences', 'Insights'],
  },
  {
    value: 'social-scheduling',
    label: 'Social Media Scheduling',
    description: 'Plan and publish posts across your social channels.',
    icon: 'CalendarClock',
    iconSrc: '/onboardingimg/socialmediascheduling.svg',
    features: ['Posts scheduling', 'Instagram', 'Facebook'],
  },
  {
    value: 'all-features',
    label: 'All features',
    description: 'Get the full WeNext suite — automation, campaigns, ads and social.',
    icon: 'Layers',
    iconSrc: '/onboardingimg/allfeatures.png',
    features: [
      'WhatsApp',
      'Instagram',
      'Campaigns',
      'Meta Ads',
      'Social scheduling',
      'Auto replies',
      'Broadcasts',
    ],
  },
];

export const INDUSTRY_OPTIONS: readonly OptionDef<BusinessIndustry>[] = [
  {
    value: 'fashion',
    label: 'Fashion and apparel',
    description: 'Clothing, footwear and accessories.',
    icon: 'Shirt',
  },
  {
    value: 'beauty',
    label: 'Beauty',
    description: 'Salons, skincare and cosmetics.',
    icon: 'Sparkles',
  },
  {
    value: 'food',
    label: 'Food and restaurants',
    description: 'Restaurants, cafés and cloud kitchens.',
    icon: 'UtensilsCrossed',
  },
  {
    value: 'healthcare',
    label: 'Healthcare',
    description: 'Clinics, dental and diagnostics.',
    icon: 'Stethoscope',
  },
  {
    value: 'education',
    label: 'Education',
    description: 'Courses, coaching and training.',
    icon: 'GraduationCap',
  },
  {
    value: 'real-estate',
    label: 'Real estate',
    description: 'Property sales and rentals.',
    icon: 'Building2',
  },
  {
    value: 'services',
    label: 'Professional services',
    description: 'Consulting, legal, finance and agencies.',
    icon: 'BriefcaseBusiness',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Something else — we will keep it general.',
    icon: 'HelpCircle',
  },
];

/** Human label for the summary / starter copy. */
export function resolveIndustryLabel(
  answers: Pick<OnboardingV2Answers, 'industry' | 'industryOther'>
): string | undefined {
  if (!answers.industry) return undefined;
  if (answers.industry === INDUSTRY_OTHER_VALUE) {
    return sanitizeIndustryOther(answers.industryOther) ?? 'Other';
  }
  return INDUSTRY_OPTIONS.find((o) => o.value === answers.industry)?.label;
}

export const OTHER_PLATFORM_OPTIONS: readonly OptionDef<TernaryAnswer>[] = [
  {
    value: 'yes',
    label: 'Yes',
    description: 'Another service currently manages this number.',
    icon: 'Link2',
  },
  {
    value: 'no',
    label: 'No',
    description: 'This number is not connected to another service.',
    icon: 'Unlink',
  },
  {
    value: 'unsure',
    label: "I'm not sure",
    description: 'We’ll help you confirm before connecting.',
    icon: 'HelpCircle',
  },
];

export interface PlatformOption {
  value: string;
  label: string;
  /**
   * Deliberately the provider's help-centre ROOT, not a deep link — vendor
   * article URLs churn and a 404 mid-migration is worse than one extra click.
   * `undefined` is a real state (`other`): we render guidance, never a dead
   * link.
   */
  helpUrl?: string;
}

/** The catch-all value. Always reachable — see `OTHER_PLATFORM_OPTION`. */
export const PLATFORM_OTHER_VALUE = 'other';

export const PLATFORM_OPTIONS: readonly PlatformOption[] = [
  { value: 'aisensy', label: 'AiSensy', helpUrl: 'https://help.aisensy.com/en/' },
  { value: 'wati', label: 'WATI', helpUrl: 'https://support.wati.io/' },
  {
    value: 'interakt',
    label: 'Interakt',
    helpUrl: 'https://www.interakt.shop/resource-center/',
  },
  { value: 'gupshup', label: 'Gupshup', helpUrl: 'https://www.gupshup.io/resources/' },
  { value: 'twilio', label: 'Twilio', helpUrl: 'https://help.twilio.com/' },
  {
    value: 'respond-io',
    label: 'respond.io',
    helpUrl: 'https://docs.respond.io/messaging-channels/whatsapp-cloud-api',
  },
  { value: 'zoko', label: 'Zoko', helpUrl: 'https://www.zoko.io/' },
  { value: 'doubletick', label: 'DoubleTick', helpUrl: 'https://doubletick.io/' },
  { value: PLATFORM_OTHER_VALUE, label: 'Another platform' },
];

/**
 * The named vendors only. The picker renders these as the FILTERABLE list and
 * pins `OTHER_PLATFORM_OPTION` above them.
 */
export const VENDOR_PLATFORM_OPTIONS: readonly PlatformOption[] = PLATFORM_OPTIONS.filter(
  (option) => option.value !== PLATFORM_OTHER_VALUE
);

/**
 * The catch-all row, rendered PINNED so it survives both scrolling and
 * filtering.
 *
 * Two bugs this fixes. As a plain 9th entry it sat below the dropdown's
 * `max-h` fold, so it read as missing; and searching for a vendor we do not
 * list filtered it out — while the empty-state hint still said "Choose Another
 * platform", pointing at a row that was no longer rendered. The extra search
 * keywords let people find it by typing what they actually type ("other",
 * "not listed", "none").
 */
export const OTHER_PLATFORM_OPTION: PlatformOption & { keywords: string[] } = {
  value: PLATFORM_OTHER_VALUE,
  label: 'Another platform',
  keywords: ['other', 'another', 'not listed', 'none', 'unknown', 'something else'],
};

export function getPlatformOption(value?: string): PlatformOption | undefined {
  if (!value) return undefined;
  return PLATFORM_OPTIONS.find((p) => p.value === value);
}

/** Human label for a stored `platformName`, or undefined when unknown. */
export function getPlatformLabel(value?: string): string | undefined {
  return getPlatformOption(value)?.label;
}

/* ------------------------------------------------------------------ *
 * Storage
 * ------------------------------------------------------------------ */

function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    // Private-mode Safari and hardened browser profiles throw on access.
    return null;
  }
}

/**
 * Read the stored draft.
 *
 * Port these five rules exactly:
 *  1. No key → `null` (first run).
 *  2. `version === 1 && answers` → migrate: keep answers, force `'goals'`.
 *  3. `version !== 2 || !answers || !currentStep` → THROW; the page renders
 *     the recovery screen.
 *  4. `currentStep === 'welcome'` (a removed step) → coerce to `'goals'`.
 *  5. Otherwise return as-is.
 *
 * @throws Error with {@link INCOMPATIBLE_DRAFT_MESSAGE} on an unusable draft.
 */
export function readOnboardingV2Draft(): OnboardingV2Draft | null {
  const store = safeLocalStorage();
  if (!store) return null;

  const raw = store.getItem(ONBOARDING_V2_DRAFT_KEY);
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error(INCOMPATIBLE_DRAFT_MESSAGE);
  }

  if (!parsed || typeof parsed !== 'object') {
    throw new Error(INCOMPATIBLE_DRAFT_MESSAGE);
  }

  const draft = parsed as Partial<OnboardingV2Draft>;

  // Rule 2 — v1 rows kept their answers but their step ids are not trusted.
  if (draft.version === 1 && draft.answers && typeof draft.answers === 'object') {
    return {
      version: ONBOARDING_V2_STORAGE_VERSION,
      answers: draft.answers as OnboardingV2Answers,
      currentStep: 'goals',
      updatedAt: typeof draft.updatedAt === 'string' ? draft.updatedAt : new Date().toISOString(),
    };
  }

  // Rule 3 — anything else that isn't a well-formed v2 row is unusable.
  if (
    draft.version !== ONBOARDING_V2_STORAGE_VERSION ||
    !draft.answers ||
    typeof draft.answers !== 'object' ||
    !draft.currentStep
  ) {
    throw new Error(INCOMPATIBLE_DRAFT_MESSAGE);
  }

  // Rule 4 — 'welcome' was removed; land such drafts on the first real step.
  const currentStep =
    (draft.currentStep as string) === 'welcome'
      ? 'goals'
      : (draft.currentStep as OnboardingV2StepId);

  return {
    version: ONBOARDING_V2_STORAGE_VERSION,
    answers: draft.answers as OnboardingV2Answers,
    currentStep,
    updatedAt: typeof draft.updatedAt === 'string' ? draft.updatedAt : new Date().toISOString(),
  };
}

/**
 * Persist the draft. Throws on a storage failure so the caller can surface the
 * "could not be saved on this device" alert and offer a retry — silently
 * swallowing it would make the save pill lie.
 */
export function writeOnboardingV2Draft(
  answers: OnboardingV2Answers,
  currentStep: OnboardingV2StepId
): void {
  const store = safeLocalStorage();
  if (!store) throw new Error('Local storage is unavailable on this device.');

  const draft: OnboardingV2Draft = {
    version: ONBOARDING_V2_STORAGE_VERSION,
    answers,
    currentStep,
    updatedAt: new Date().toISOString(),
  };
  store.setItem(ONBOARDING_V2_DRAFT_KEY, JSON.stringify(draft));
}

/** Removes the draft AND the connected flag — a true first-run reset. */
export function clearOnboardingV2Draft(): void {
  const store = safeLocalStorage();
  if (!store) return;
  try {
    store.removeItem(ONBOARDING_V2_DRAFT_KEY);
    store.removeItem(ONBOARDING_V2_CONNECTED_KEY);
  } catch {
    // Best effort — a failed clear must never break "Start over".
  }
}

export function markOnboardingV2Connected(): void {
  const store = safeLocalStorage();
  if (!store) return;
  try {
    store.setItem(ONBOARDING_V2_CONNECTED_KEY, 'true');
  } catch {
    // Best effort — the connection itself already succeeded.
  }
}

export function hasCompletedOnboardingV2Connection(): boolean {
  const store = safeLocalStorage();
  if (!store) return false;
  try {
    return store.getItem(ONBOARDING_V2_CONNECTED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function isInstagramConnectedLocally(): boolean {
  const store = safeLocalStorage();
  if (!store) return false;
  try {
    return Boolean(store.getItem(INSTAGRAM_CONNECTED_AT_KEY));
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------ *
 * Step machine
 * ------------------------------------------------------------------ */

export function needsWhatsAppSetup(answers: OnboardingV2Answers): boolean {
  const goals = expandGoals(answers.goals || []);
  return goals.includes('whatsapp-automation') || goals.includes('campaigns-broadcasts');
}

export function needsInstagramSetup(answers: OnboardingV2Answers): boolean {
  return expandGoals(answers.goals || []).includes('instagram-automation');
}

/**
 * The ordered list of steps this user will actually see.
 *
 * `'success'` is never included — see invariant 1 at the top of this file.
 */
export function buildStepSequence(answers: OnboardingV2Answers): OnboardingV2StepId[] {
  const steps: OnboardingV2StepId[] = ['goals'];

  // Escape hatch: with no goal chosen there is nothing to personalize, so the
  // sequence is just the entry step + the summary. (The Continue button is
  // disabled in this state, so it is not normally reachable.)
  if (!answers.goals?.length) {
    steps.push('summary');
    return steps;
  }

  // Always 2nd — the industry personalizes every later preview.
  steps.push('business-type');

  // The "connected to another platform?" step is intentionally hidden from the
  // live flow. Legacy drafts may still carry `otherPlatform` / `platformName`
  // answers; readiness / guidance still honour those when present.

  steps.push('summary');
  return steps;
}

/** Steps that count towards the "Step N of M" label — everything but summary. */
export function getDecisionSteps(sequence: OnboardingV2StepId[]): OnboardingV2StepId[] {
  return sequence.filter((s) => s !== 'summary');
}

/**
 * Floor for the progress denominator. The live flow is always
 * goals → business-type once a goal is chosen; before that the sequence is
 * truncated and must not report 100% on the first screen.
 */
export const MIN_DECISION_STEPS = 2;

export function isStepComplete(
  step: OnboardingV2StepId,
  answers: OnboardingV2Answers
): boolean {
  switch (step) {
    case 'goals':
      return (answers.goals?.length || 0) > 0;
    case 'business-type':
      if (!answers.industry) return false;
      if (answers.industry === INDUSTRY_OTHER_VALUE) {
        return Boolean(sanitizeIndustryOther(answers.industryOther));
      }
      return true;
    case 'other-platform':
      // "Yes" needs a provider pick on the same step (inline dropdown).
      if (answers.otherPlatform === 'yes') return Boolean(answers.platformName);
      return Boolean(answers.otherPlatform);
    case 'platform-name':
      // Legacy id — the picker now lives on `other-platform`. Kept so old
      // drafts / resume links still resolve a sensible completeness check.
      return Boolean(answers.platformName);
    case 'summary':
    case 'success':
      return true;
    default:
      return false;
  }
}

/**
 * Single-choice radio steps that advance themselves.
 *
 * `goals` is excluded because it is multi-select — there is no "the answer".
 * `other-platform` is excluded because that step is hidden from the live flow.
 */
export const AUTO_ADVANCE_STEPS: ReadonlySet<OnboardingV2StepId> = new Set<OnboardingV2StepId>([
  'business-type',
]);

export const AUTO_ADVANCE_DELAY_MS = 320;

/* ------------------------------------------------------------------ *
 * Readiness
 * ------------------------------------------------------------------ */

export type ReadinessTone = 'ready' | 'action' | 'attention';
export type ReadinessId = 'platform';

export interface ReadinessItem {
  id: ReadinessId;
  title: string;
  detail: string;
  tone: ReadinessTone;
  /** Step the row's CTA resumes into. */
  resumeStep: OnboardingV2StepId;
  ctaLabel: string;
}

/**
 * Derive the readiness checklist.
 *
 * Only produced when {@link needsWhatsAppSetup} is true — an Instagram-only
 * user needs no number preparation, so the list is empty and the summary shows
 * a reassurance panel instead.
 *
 * The platform row is the ONLY row. Every other candidate (manager access,
 * two-step PIN, how the number is used, whether the phone can receive a code)
 * was removed from the flow, and no item may be derived from a question we do
 * not ask — a row derived from an absent answer would sit at `attention`
 * forever, permanently blocking every WhatsApp user.
 */
export function getReadinessItems(answers: OnboardingV2Answers): ReadinessItem[] {
  if (!needsWhatsAppSetup(answers)) return [];

  const items: ReadinessItem[] = [];

  // --- platform -----------------------------------------------------
  if (answers.otherPlatform === 'yes') {
    const label = getPlatformLabel(answers.platformName) || 'Your current platform';
    items.push({
      id: 'platform',
      title: `${label} needs a migration plan`,
      detail: 'We’ll help migrate your number safely before connecting it.',
      tone: 'action',
      resumeStep: 'summary',
      ctaLabel: 'Plan migration',
    });
  } else if (answers.otherPlatform === 'no') {
    items.push({
      id: 'platform',
      title: 'No previous platform reported',
      detail: 'There is no platform handover in your current setup plan.',
      tone: 'ready',
      resumeStep: 'summary',
      ctaLabel: 'Review platform',
    });
  } else {
    // unset or 'unsure' — READY, not a blocker. We defer to Meta rather than
    // claiming to identify the provider ourselves.
    items.push({
      id: 'platform',
      title: 'Registration check planned',
      detail:
        'Meta can flag an existing registration after you sign in, but it may not identify the current provider.',
      tone: 'ready',
      resumeStep: 'summary',
      ctaLabel: 'Review answer',
    });
  }

  return items;
}

export function isReadyForMeta(answers: OnboardingV2Answers): boolean {
  if (!needsWhatsAppSetup(answers)) return false;
  return getReadinessItems(answers).every((item) => item.tone === 'ready');
}

export function getRemainingTasks(answers: OnboardingV2Answers): ReadinessItem[] {
  return getReadinessItems(answers).filter((item) => item.tone !== 'ready');
}

/* ------------------------------------------------------------------ *
 * Goal setup plan
 * ------------------------------------------------------------------ */

export type GoalPlanKind = 'whatsapp' | 'instagram' | 'automation' | 'campaign' | 'ads' | 'social';

export interface GoalPlanItem {
  id: string;
  kind: GoalPlanKind;
  title: string;
  detail: string;
  actionLabel: string;
  /** `undefined` means the action opens a dialog instead of navigating. */
  route?: string;
}

/**
 * Dependency-ordered actions. Connections come before the things that depend
 * on them, so a user cannot be sent to build an automation for a channel they
 * have not connected yet.
 */
export function getGoalSetupPlan(answers: OnboardingV2Answers): GoalPlanItem[] {
  const goals = expandGoals(answers.goals || []);
  const plan: GoalPlanItem[] = [];

  if (needsWhatsAppSetup(answers)) {
    plan.push({
      id: 'connect-whatsapp',
      kind: 'whatsapp',
      title: 'Connect WhatsApp',
      detail: 'Choose guided setup, connect directly, or book a call with WeNext.',
      actionLabel: 'Connect WhatsApp',
    });
  }

  if (needsInstagramSetup(answers)) {
    plan.push({
      id: 'connect-instagram',
      kind: 'instagram',
      title: 'Connect Instagram',
      detail: 'Sign in to the Business or Creator account you want to use.',
      actionLabel: 'Connect Instagram',
      route: '/home/social-media/instagram',
    });
  }

  if (goals.includes('whatsapp-automation')) {
    plan.push({
      id: 'create-whatsapp-automation',
      kind: 'automation',
      title: 'Create your first automatic reply',
      detail: 'Build one simple reply or follow-up, then test it.',
      actionLabel: 'Create automation',
      route: '/home/automation',
    });
  }

  if (goals.includes('instagram-automation')) {
    plan.push({
      id: 'create-instagram-automation',
      kind: 'automation',
      title: 'Create your first Instagram reply',
      detail: 'Choose one enquiry or DM response to automate.',
      actionLabel: 'Create Instagram automation',
      route: '/home/automation/instagram',
    });
  }

  if (goals.includes('campaigns-broadcasts')) {
    plan.push({
      id: 'launch-campaign',
      kind: 'campaign',
      title: 'Send your first campaign',
      detail: 'Create a message, choose customers, then review before sending.',
      actionLabel: 'Create a campaign',
      route: '/home/campaigns',
    });
  }

  if (goals.includes('meta-ads')) {
    plan.push({
      id: 'setup-meta-ads',
      kind: 'ads',
      title: 'Set up Meta Ads',
      detail: 'Connect your ad account and launch or manage campaigns.',
      actionLabel: 'Run Meta Ads',
      route: '/home/ads',
    });
  }

  if (goals.includes('social-scheduling')) {
    plan.push({
      id: 'schedule-social-posts',
      kind: 'social',
      title: 'Schedule your first social post',
      detail: 'Plan posts for Instagram, Facebook and other connected channels.',
      actionLabel: 'Schedule a post',
      route: '/home/social-media/posts',
    });
  }

  return plan;
}

/* ------------------------------------------------------------------ *
 * Chrome helpers (progress label, %, time remaining)
 * ------------------------------------------------------------------ */

export interface ProgressInfo {
  label: string;
  percent: number;
  /** Index of `currentStep` within the decision steps; -1 when absent. */
  decisionIndex: number;
  decisionCount: number;
}

export function getProgressInfo(
  step: OnboardingV2StepId,
  answers: OnboardingV2Answers
): ProgressInfo {
  const sequence = buildStepSequence(answers);
  const decisionSteps = getDecisionSteps(sequence);
  const decisionIndex = decisionSteps.indexOf(step);
  // Before any goal is chosen, buildStepSequence collapses to a single decision
  // step (`goals` → `summary`). Using that length as the denominator makes
  // "Step 1" read as 100% — the bar looks finished on the first screen. The
  // live journey is always goals → business-type once a goal is picked, so the
  // chrome always measures against at least those two decision steps.
  const decisionCount = Math.max(decisionSteps.length, MIN_DECISION_STEPS);

  if (step === 'summary') {
    return { label: 'Personalized summary', percent: 100, decisionIndex, decisionCount };
  }
  if (step === 'success') {
    return { label: 'Connection status', percent: 100, decisionIndex, decisionCount };
  }

  const label =
    step === 'goals' && !answers.goals?.length
      ? 'Step 1'
      : decisionIndex < 0
        ? 'Step 1'
        : `Step ${decisionIndex + 1} of ${decisionCount}`;

  // Percent matches the step label: step N of M → (N/M)×100. Unknown / empty
  // sequences stay at 0 rather than inventing a full bar.
  const percent =
    decisionIndex < 0 || decisionCount <= 0
      ? 0
      : Math.min(100, Math.round(((decisionIndex + 1) / decisionCount) * 100));

  return { label, percent, decisionIndex, decisionCount };
}

/** `null` means "render nothing" (the success step). */
export function getTimeRemainingLabel(
  step: OnboardingV2StepId,
  answers: OnboardingV2Answers
): string | null {
  if (step === 'success') return null;
  if (step === 'goals' && !answers.goals?.length) return 'Less than a minute';

  const { decisionIndex, decisionCount } = getProgressInfo(step, answers);
  const remaining = Math.max(0, decisionCount - decisionIndex - 1);
  if (remaining > 0) return `About ${Math.max(15, remaining * 8)} seconds left`;
  return 'Final review';
}

/**
 * Resolve which step to land on during hydration.
 *
 * A `?resume=` step the current answers do not unlock falls back to `'goals'`
 * rather than rendering an orphan question.
 */
export function resolveResumeStep(
  draft: OnboardingV2Draft | null,
  resumeParam: string | null
): OnboardingV2StepId {
  const requested = VALID_RESUME_STEPS.includes(resumeParam as OnboardingV2StepId)
    ? (resumeParam as OnboardingV2StepId)
    : null;

  // No draft ⇒ no answers ⇒ nothing to resume into.
  if (!draft) return 'goals';

  const preferredRaw = requested ?? draft.currentStep;
  // The other-platform / platform-name steps are hidden. Old drafts and deep
  // links that name them land on the summary (or goals if there is nothing to
  // summarize yet) instead of an orphan question.
  if (preferredRaw === 'platform-name' || preferredRaw === 'other-platform') {
    if (!draft.answers.goals?.length) return 'goals';
    // WhatsApp drafts were mid-handover → land on the plan. Instagram-only
    // never asked this question, so a stale deep link falls back to goals.
    return needsWhatsAppSetup(draft.answers) ? 'summary' : 'goals';
  }
  const preferred = preferredRaw;

  // 'success' is terminal and never in a sequence, so it is allowed through
  // explicitly rather than by membership.
  if (preferred === 'success') return 'success';

  const validSequence = buildStepSequence(draft.answers);
  return validSequence.includes(preferred) ? preferred : 'goals';
}
